import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../../shared/Icons';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';

const CommunityNews = () => {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // AI 요약 관련 상태
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // 카테고리 로드 (전체 카테고리)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/news/categories`);
        const data = await response.json();
        if (data.success) {
          setCategories(data.categories);
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // 뉴스 로드
  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken ? getToken() : null;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30'
      });
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/news?${params}`, {
        headers
      });

      const data = await response.json();
      if (data.success) {
        setNews(data.articles);
        setTotalPages(data.totalPages);
      } else {
        setError(data.error || '뉴스를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError('뉴스를 불러오는데 실패했습니다.');
      console.error('Failed to load news:', err);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory, getToken]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  // 카테고리 요약 로드
  const fetchCategorySummary = useCallback(async (category, refresh = false) => {
    if (!category) {
      setSummary(null);
      return;
    }

    setSummaryLoading(true);
    try {
      const params = refresh ? '?refresh=true' : '';
      const response = await fetch(`${API_BASE_URL}/api/news/summary/${category}${params}`);
      const data = await response.json();

      if (data.success) {
        setSummary(data);
        setShowSummary(true);
      }
    } catch (err) {
      console.error('Failed to load category summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // 카테고리 변경 시 요약 로드
  useEffect(() => {
    if (selectedCategory) {
      fetchCategorySummary(selectedCategory);
    } else {
      setSummary(null);
      setShowSummary(false);
    }
  }, [selectedCategory, fetchCategorySummary]);

  // 카테고리 라벨
  const getCategoryLabel = (category) => {
    const labels = {
      headlines: '헤드라인',
      business: '비즈니스',
      technology: '기술',
      entertainment: '연예',
      sports: '스포츠',
      world: '세계',
      ai: 'AI/인공지능',
      startup: '스타트업',
      security: '보안',
      economy: '경제',
      stock: '증권/주식',
      realestate: '부동산',
      industry: '산업',
      childcare: '육아',
      education: '교육',
      healthcare: '의료'
    };
    return labels[category] || category;
  };

  // 카테고리 아이콘
  const getCategoryIcon = (category) => {
    const icons = {
      headlines: 'news',
      business: 'briefcase',
      technology: 'cpu',
      entertainment: 'film',
      sports: 'trophy',
      world: 'globe',
      ai: 'brain',
      startup: 'rocket',
      security: 'shield',
      economy: 'trendingUp',
      stock: 'chart',
      realestate: 'home',
      industry: 'factory',
      childcare: 'baby',
      education: 'book',
      healthcare: 'heart'
    };
    return icons[category] || 'news';
  };

  // 날짜 포맷 (날짜별 그룹핑용)
  const formatDateGroup = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const articleDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (articleDate.getTime() === today.getTime()) {
      return '오늘';
    } else if (articleDate.getTime() === yesterday.getTime()) {
      return '어제';
    } else {
      return date.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
    }
  };

  // 시간 포맷
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  };

  // 날짜별로 뉴스 그룹핑
  const groupNewsByDate = (articles) => {
    const groups = {};
    articles.forEach(article => {
      const dateKey = formatDateGroup(article.pub_date);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(article);
    });
    return groups;
  };

  const groupedNews = groupNewsByDate(news);

  // 카테고리 배지 색상 클래스
  const getCategoryBadgeClass = (category) => {
    const categoryClasses = {
      entertainment: 'bg-pink-100 text-pink-700',
      sports: 'bg-blue-100 text-blue-700',
      headlines: 'bg-red-100 text-red-700',
      business: 'bg-indigo-100 text-indigo-700',
      technology: 'bg-cyan-100 text-cyan-700',
      economy: 'bg-emerald-100 text-emerald-700',
      stock: 'bg-emerald-100 text-emerald-700',
      ai: 'bg-violet-100 text-violet-700',
      world: 'bg-green-100 text-green-800',
      childcare: 'bg-amber-100 text-amber-700',
      education: 'bg-purple-100 text-purple-700',
      healthcare: 'bg-pink-100 text-pink-600'
    };
    return categoryClasses[category] || 'bg-gray-100 text-gray-600';
  };

  if (loading && page === 1) {
    return (
      <div className="max-w-[900px] mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-gray-500">뉴스를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[900px] mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 text-gray-500">
          <Icon name="warning" size={48} className="text-amber-500" />
          <p>{error}</p>
          <button
            onClick={fetchNews}
            className="mt-2 py-2 px-6 bg-blue-500 text-white border-none rounded-lg cursor-pointer text-[15px] hover:bg-blue-600"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 m-0 mb-1">뉴스</h1>
        <p className="text-[15px] text-gray-500 m-0">최신 뉴스를 한눈에</p>
      </div>

      {/* 카테고리 필터 */}
      <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-slate-200">
        <button
          className={`py-1.5 px-3.5 bg-slate-50 border border-slate-200 rounded-full text-xs text-gray-500 cursor-pointer transition-all duration-200 hover:bg-white hover:text-gray-900 ${selectedCategory === '' ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600 hover:text-white hover:border-blue-600' : ''}`}
          onClick={() => { setSelectedCategory(''); setPage(1); }}
        >
          전체
        </button>
        {categories.map(cat => (
          <button
            key={cat.value}
            className={`py-1.5 px-3.5 bg-slate-50 border border-slate-200 rounded-full text-xs text-gray-500 cursor-pointer transition-all duration-200 hover:bg-white hover:text-gray-900 ${selectedCategory === cat.value ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600 hover:text-white hover:border-blue-600' : ''}`}
            onClick={() => { setSelectedCategory(cat.value); setPage(1); }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* AI 요약 섹션 */}
      {selectedCategory && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0">
              <Icon name="brain" size={20} />
              AI 테마 요약
            </h2>
            <div className="flex gap-2">
              <button
                className="inline-flex items-center gap-1 py-1.5 px-3 bg-white border border-slate-200 rounded-lg text-xs text-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
                onClick={() => setShowSummary(!showSummary)}
              >
                {showSummary ? '접기' : '펼치기'}
              </button>
              <button
                className="inline-flex items-center gap-1 py-1.5 px-3 bg-white border border-slate-200 rounded-lg text-xs text-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => fetchCategorySummary(selectedCategory, true)}
                disabled={summaryLoading}
              >
                <Icon name="refresh" size={14} />
                새로고침
              </button>
            </div>
          </div>

          {summaryLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-gray-500 text-[15px]">
              <div className="w-5 h-5 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
              <span>AI가 뉴스를 분석 중...</span>
            </div>
          ) : showSummary && summary ? (
            <div className="flex flex-col gap-6">
              {/* 전체 요약 */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg">
                <p className="text-[15px] text-gray-900 leading-relaxed m-0 mb-2">{summary.summary}</p>
                <span className="text-xs text-gray-400">
                  {summary.totalArticles}건 분석 · {new Date(summary.generatedAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} 기준
                </span>
              </div>

              {/* 핫 키워드 */}
              {summary.hotKeywords && summary.hotKeywords.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 m-0 mb-2">핫 키워드</h3>
                  <div className="flex flex-wrap gap-1">
                    {summary.hotKeywords.slice(0, 10).map((kw, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 py-1 px-2.5 bg-white border border-slate-200 rounded-full text-xs text-gray-900 transition-all duration-200 hover:bg-blue-500 hover:text-white hover:border-blue-500">
                        {kw.word}
                        <span className="bg-black/10 py-0.5 px-1.5 rounded-full text-[10px] text-gray-400">{kw.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 테마별 요약 */}
              {summary.themes && summary.themes.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 m-0 mb-2">테마별 요약</h3>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
                    {summary.themes.map((theme, idx) => (
                      <div key={idx} className="bg-white border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[15px] font-semibold text-blue-500">{theme.keyword}</span>
                          <span className="text-xs text-gray-400">{theme.articleCount}건</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-normal m-0 mb-2">{theme.summary}</p>
                        <div className="flex flex-col gap-1.5 pt-2 border-t border-slate-200">
                          {theme.articles.slice(0, 3).map((article, i) => (
                            <div key={i} className="flex justify-between items-start gap-2">
                              <span className="text-xs text-gray-900 leading-snug overflow-hidden text-ellipsis line-clamp-2">{article.title}</span>
                              <span className="text-[10px] text-gray-400 flex-shrink-0">{article.source}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* 뉴스 목록 (날짜별 그룹) */}
      <div>
        {Object.keys(groupedNews).length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 text-gray-400">
            <Icon name="news" size={48} className="opacity-50" />
            <p>뉴스가 없습니다.</p>
          </div>
        ) : (
          Object.entries(groupedNews).map(([date, articles]) => (
            <div key={date} className="mb-8">
              <h2 className="text-base font-semibold text-gray-900 m-0 mb-4 pb-2 border-b-2 border-blue-500 inline-block">{date}</h2>
              <div className="flex flex-col gap-2">
                {articles.map(article => (
                  <article key={article.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4 transition-all duration-200 hover:border-blue-500 hover:shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
                    <div className="flex justify-between items-center mb-2 gap-2">
                      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                        <span className={`text-[11px] font-medium py-0.5 px-2 rounded ${getCategoryBadgeClass(article.category)}`}>
                          {getCategoryLabel(article.category)}
                        </span>
                        {article.keywords && (
                          <div className="flex items-center gap-1 flex-wrap">
                            {article.keywords.split(',').slice(0, 3).map((kw, idx) => (
                              <span key={idx} className="text-[11px] text-blue-500 bg-blue-50 py-0.5 px-1.5 rounded whitespace-nowrap">#{kw.trim()}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-[11px] text-gray-400">{formatTime(article.pub_date)}</span>
                    </div>
                    <h3 className="text-[15px] font-medium m-0 mb-1 leading-snug">
                      <a href={article.link} target="_blank" rel="noopener noreferrer" className="text-gray-900 no-underline hover:text-blue-500">
                        {article.title}
                      </a>
                    </h3>
                    {article.description && (
                      <p className="text-xs text-gray-500 m-0 mb-2 leading-normal">
                        {article.description.length > 100
                          ? article.description.substring(0, 100) + '...'
                          : article.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4">
                      <span className="text-[11px] text-gray-400">{article.source}</span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8 pt-6 border-t border-slate-200">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="py-2 px-6 bg-slate-50 border border-slate-200 rounded-lg text-[15px] text-gray-900 cursor-pointer transition-all duration-200 hover:bg-blue-500 hover:text-white hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              이전
            </button>
            <span className="text-[15px] text-gray-500">{page} / {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="py-2 px-6 bg-slate-50 border border-slate-200 rounded-lg text-[15px] text-gray-900 cursor-pointer transition-all duration-200 hover:bg-blue-500 hover:text-white hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityNews;
